import { Field, Item, getFieldValue, useSitecoreContext, withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames/bind';
import React from 'react';
import { RecordOfFields } from 'models/fields';

import { ComponentProps } from 'lib/component-props';
import compose from 'lib/enhancers/compose';
import withStyles from 'lib/enhancers/withStyles';

import styles from './Video.module.scss';

interface VideoFields {
  videoId: Field<string>;
  kalturaId: Field<string>;
  kalturaPlayerType: Item;
}

type VideoProps = ComponentProps & {
  fields: VideoFields;
};

const cx = classNames.bind(styles);

const Video = (props: VideoProps): JSX.Element => {
  const { className, fields } = props;

  const embedCode = fields.videoId.value;
  const kalturaCode = fields.kalturaId.value;
  const embedClassName = `wistia_async_${embedCode}`;
  const playerId = 'video_id_' + props.rendering.uid;

  const parentRef = React.useRef<HTMLDivElement>(null);
  const imageUrl = `https://fast.wistia.com/embed/medias/${embedCode}/swatch?${Date.now()}`;

  const kalturaPlayerTypeItemField = fields.kalturaPlayerType?.fields as RecordOfFields;
  const kalturaPlayerTypeValue = getFieldValue<string>(kalturaPlayerTypeItemField, 'playerID') || '55870672';
  const { sitecoreContext } = useSitecoreContext();

  React.useEffect(() => {
    if (kalturaCode != '') {
      const scriptk = document.createElement('script');
      if (kalturaPlayerTypeValue.startsWith('516')) {
        scriptk.src =
          'https://cdnapisec.kaltura.com/p/6105852/sp/6105852100/embedIframeJs/uiconf_id/' +
          kalturaPlayerTypeValue +
          '/partner_id/6105852?autoembed=true&entry_id=' +
          kalturaCode +
          '&playerId=' +
          playerId +
          '&cache_st=1730822117&width=600&height=337';
      } else {
        scriptk.src =
          'https://cdnapisec.kaltura.com/p/6105852/embedPlaykitJs/uiconf_id/' +
          kalturaPlayerTypeValue +
          '?autoembed=true&targetId=' +
          playerId +
          '&entry_id=' +
          kalturaCode;
      }
      scriptk.type = 'text/javascript';
      scriptk.async = true;
      // Check if script1 is not already present on the page
      if (!document.querySelector(`script[src="${scriptk.src}"]`)) {
        document.body.appendChild(scriptk);
      }
    } else {
      const script1 = document.createElement('script');
      script1.src = 'https://fast.wistia.com/assets/external/E-v1.js';
      script1.async = true;
      // Check if script1 is not already present on the page
      if (!document.querySelector(`script[src="${script1.src}"]`)) {
        document.body.appendChild(script1);
      }

      const script2 = document.createElement('script');
      script2.src = `https://fast.wistia.com/embed/medias/${embedCode}.jsonp`;
      script2.async = true;
      document.body.appendChild(script2);
    }
  }, [embedCode, kalturaCode, playerId, kalturaPlayerTypeValue, sitecoreContext.pageEditing]);

  const handleImageLoad = () => {
    if (parentRef.current) {
      parentRef.current.style.opacity = '1';
    }
  };

  let rtrn = <></>;

  if (fields.kalturaId.value != '') {
    rtrn = (
      <div className={cx('responsive_padding', 'video-padding', className)}>
        <div className={cx('responsive_wrapper', 'video-wrapper')}>
          <div id={playerId} style={{ width: '100%', height: '100%' }}></div>
        </div>
      </div>
    );
  } else {
    rtrn = (
      <div className={cx('wistia_responsive_padding', 'wistia-video-padding', className)}>
        <div className={cx('wistia_responsive_wrapper', 'wistia-video-wrapper')}>
          <div className={cx({ [embedClassName]: true }, 'wistia_embed', 'wistia-video-embed', 'videoFoam=true')}>
            <div className={cx('wistia_swatch', 'wistia-video-swatch')} ref={parentRef}>
              <img
                onLoad={handleImageLoad}
                src={imageUrl}
                className={cx('wistia-video-img')}
                alt=""
                aria-hidden="true"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
  return rtrn;
};

export default compose<VideoProps>(withDatasourceCheck(), withStyles())(Video);
